import { useCallback, useMemo } from 'react';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

import { zonesStore, type ZonesStoreState } from './ZonesStore';
import { selectData, selectById, selectIsLoading } from '../selectors.ts';
import { buildTree } from '../../lib/buildTree.ts';

import { useAttach } from '../useAttach';

export function useZonesData() {
  useAttach(zonesStore, 'useZonesData');
  return useStoreWithEqualityFn(zonesStore.store, selectData, shallow);
}
useZonesData.store = zonesStore;

export function useZonesById() {
  useAttach(zonesStore, 'useZonesById');
  return useStoreWithEqualityFn(zonesStore.store, selectById, shallow);
}
useZonesById.store = zonesStore;

export function useZone({ zoneId }: { zoneId: string }) {
  useAttach(zonesStore, 'useZone');

  const select = useCallback(
    (state: ZonesStoreState) => {
      return {
        zone: state.byId?.[zoneId] ?? null,
        zoneInstance: state.data?.[zoneId] ?? null,
        isLoading: state.isLoading,
      };
    },
    [zoneId]
  );

  return useStoreWithEqualityFn(zonesStore.store, select, shallow);
}
useZone.store = zonesStore;

export function useRootZone() {
  useAttach(zonesStore, 'useRootZone');

  const select = useCallback((state: ZonesStoreState) => {
    const rootZoneId = state.rootZoneId;

    if (rootZoneId == null) {
      return { rootZone: null, rootZoneInstance: null };
    }

    return {
      rootZone: state.byId?.[rootZoneId] ?? null,
      rootZoneInstance: state.data?.[rootZoneId] ?? null,
    };
  }, []);

  return useStoreWithEqualityFn(zonesStore.store, select, shallow);
}
useRootZone.store = zonesStore;

export function useZonesAttach() {
  useAttach(zonesStore, 'useZonesAttach');
}
useZonesAttach.store = zonesStore;

export function useZonesLoading() {
  useAttach(zonesStore, 'useZonesLoading');
  return useStoreWithEqualityFn(zonesStore.store, selectIsLoading, Object.is);
}
useZonesLoading.store = zonesStore;

export function useZonesTree() {
  useAttach(zonesStore, 'useZonesTree');

  const select = useCallback((state: ZonesStoreState) => {
    return {
      isLoading: state.isLoading,
      error: state.error,
      byId: state.byId,
      data: state.data,
    };
  }, []);

  const result = useStoreWithEqualityFn(zonesStore.store, select, shallow);

  const parentsOrder = useMemo(() => {
    const zoneParentMapping = new Set<string>();

    for (const [zoneId, zone] of Object.entries(result.byId ?? {})) {
      zoneParentMapping.add(zoneId + zone.parent);
    }

    return Array.from(zoneParentMapping).sort().join(',');
  }, [result.byId]);

  const sortIndexOrder = useMemo(() => {
    const zoneSortIndexMapping = new Set<string>();

    for (const [zoneId, zone] of Object.entries(result.byId ?? {})) {
      zoneSortIndexMapping.add(zoneId + (zone.sortIndex ?? ''));
    }

    return Array.from(zoneSortIndexMapping).sort().join(',');
  }, [result.byId]);

  const tree = useMemo(() => {
    return buildTree(result.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.data, parentsOrder, sortIndexOrder]);

  return { ...result, tree };
}
useZonesTree.store = zonesStore;

export function useRootZoneId() {
  useAttach(zonesStore, 'useRootZoneId');

  const select = useCallback((state: ZonesStoreState) => {
    return {
      rootZoneId: state.rootZoneId,
    };
  }, []);

  return useStoreWithEqualityFn(zonesStore.store, select, shallow);
}
useRootZoneId.store = zonesStore;

export function useZonesByIdChildren() {
  useAttach(zonesStore, 'useZonesByIdChildren');

  const select = useCallback((state: ZonesStoreState) => {
    const byId = state.byId ?? {};
    const byIdChildren: { [key: string]: Set<string> } = {};

    for (const zoneId of Object.keys(byId)) {
      const zone = byId[zoneId];

      if (byIdChildren[zone.parent ?? 'null'] == null) {
        byIdChildren[zone.parent ?? 'null'] = new Set();
      }

      byIdChildren[zone.parent ?? 'null'].add(zoneId);
    }

    return { byIdChildren, data: state.data, isLoading: state.isLoading, error: state.error };
  }, []);

  return useStoreWithEqualityFn(zonesStore.store, select, shallow);
}
useZonesByIdChildren.store = zonesStore;
